// Client-side media compression for Sell Devices uploads.

export async function compressImage(file, maxDim = 1280, quality = 0.72) {
  if (!file.type.startsWith("image/")) return file;
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const s = maxDim / Math.max(width, height);
    width = Math.round(width * s); height = Math.round(height * s);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
  if (!blob || blob.size >= file.size) return file;
  return new File([blob], (file.name || "photo").replace(/\.\w+$/, "") + ".jpg", { type: "image/jpeg" });
}

// Best-effort video compression targeting ~targetMB by re-encoding at reduced
// resolution + computed bitrate via canvas + MediaRecorder. Falls back to the
// original file if the browser can't re-encode.
export async function compressVideo(file, targetMB = 5, maxDim = 640) {
  if (!file.type.startsWith("video/")) return file;
  if (file.size <= targetMB * 1024 * 1024) return file;
  try {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    video.muted = true; video.playsInline = true; video.src = url;
    await new Promise((res, rej) => { video.onloadedmetadata = res; video.onerror = rej; });
    const duration = video.duration || 30;
    let w = video.videoWidth || maxDim, h = video.videoHeight || maxDim;
    if (w > maxDim || h > maxDim) { const s = maxDim / Math.max(w, h); w = Math.round(w * s); h = Math.round(h * s); }
    const targetBits = targetMB * 8 * 1024 * 1024 * 0.9;
    const bitrate = Math.max(250000, Math.min(1500000, Math.floor(targetBits / duration)));
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d");
    const stream = canvas.captureStream(24);
    const mime = MediaRecorder.isTypeSupported("video/webm;codecs=vp8") ? "video/webm;codecs=vp8" : "video/webm";
    const rec = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: bitrate });
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    const done = new Promise((res) => { rec.onstop = res; });
    rec.start();
    video.currentTime = 0;
    await video.play().catch(() => {});
    await new Promise((res) => {
      const draw = () => {
        if (video.paused || video.ended) { return; }
        ctx.drawImage(video, 0, 0, w, h);
        requestAnimationFrame(draw);
      };
      draw();
      video.onended = () => res();
      // safety cap
      setTimeout(() => res(), (duration + 1) * 1000);
    });
    rec.stop();
    await done;
    URL.revokeObjectURL(url);
    const blob = new Blob(chunks, { type: "video/webm" });
    if (!blob.size) return file;
    return new File([blob], (file.name || "video").replace(/\.\w+$/, "") + ".webm", { type: "video/webm" });
  } catch (e) {
    return file; // fallback: upload original (backend enforces 12MB cap)
  }
}

export async function compressMedia(file, mediaType) {
  return mediaType === "video" ? compressVideo(file) : compressImage(file);
}
